import { useFormikContext } from 'formik';
import { FC, useCallback, useMemo, useState } from 'react';

import { LoadingSpinner } from '@components/uiKit/LoadingSpinner';

import { FormValues } from '../../../../types';
import { getFieldName, isBrandFilter, isNumberArray, isPriceRangeDefault } from '../../../../utils';
import ActiveFiltersSection from '../ActiveFiltersSection';
import FiltersFooter from '../FiltersFooter';
import IsPreOrderSwitcher from '../IsPreOrderSwitcher';
import PriceFilterSection from '../PriceFilterSection';
import BrandFiltersSection from './components/BrandFiltersSection';
import MobileFilterItem from './components/MobileFilterItem';
import { loaderOverlayStyles, mobileRootStyles, popupBodyStyles, scrollableContainerStyles } from './styles';
import { MobileFiltersPopupContentProps } from './types';

const MobileFiltersPopupContent: FC<MobileFiltersPopupContentProps> = ({
    isLoading,
    isBrandPage = false,
    filterData,
    fieldPrefix,
    currentPriceRange,
    currentTotalCount,
    hidePriceFilter,
    priceRange,
    rangeDefault,
    currentFilterData,
    onApplyClick,
    onFilterChange,
}) => {
    const { initialValues } = useFormikContext<FormValues>();

    const [expandedFilters, setExpandedFilters] = useState<{ [key: string]: boolean }>({});

    const priceFieldName = getFieldName('price', fieldPrefix);
    const priceValue = initialValues[priceFieldName];
    const savedPrice = priceValue !== undefined && isNumberArray(priceValue) ? priceValue : undefined;
    const priceFromUrl = savedPrice;

    const [rememberedPrice, setRememberedPrice] = useState<number[] | undefined>(() => {
        if (savedPrice && priceRange) {
            const [min, max] = savedPrice;
            if (!isPriceRangeDefault(min, max, priceRange)) {
                return savedPrice;
            }
        }

        return undefined;
    });

    const toggleFilter = useCallback((filterCode: string) => {
        setExpandedFilters(prev => ({
            ...prev,
            [filterCode]: !prev[filterCode],
        }));
    }, []);

    const currentRangeDefault = useMemo(() => {
        return currentPriceRange ? [currentPriceRange.min, currentPriceRange.max] : rangeDefault;
    }, [currentPriceRange, rangeDefault]);

    return (
        <div css={mobileRootStyles}>
            <div css={popupBodyStyles}>
                {isLoading && (
                    <div css={loaderOverlayStyles}>
                        <LoadingSpinner embedded noPadding />
                    </div>
                )}
                <ActiveFiltersSection
                    filterData={filterData}
                    fieldPrefix={fieldPrefix}
                    currentPriceRange={currentPriceRange}
                    currentRangeDefault={currentRangeDefault}
                    rememberedPrice={rememberedPrice}
                    setRememberedPrice={setRememberedPrice}
                    onFilterChange={onFilterChange}
                    hideClearButton
                    containerCss={{ padding: '8px 16px' }}
                />
                <div css={scrollableContainerStyles}>
                    <PriceFilterSection
                        hidePriceFilter={hidePriceFilter}
                        fieldPrefix={fieldPrefix}
                        currentPriceRange={currentPriceRange}
                        priceRange={priceRange}
                        rangeDefault={rangeDefault}
                        rememberedPrice={rememberedPrice}
                        priceFromUrl={priceFromUrl}
                        setRememberedPrice={setRememberedPrice}
                        onFilterChange={onFilterChange}
                    />

                    <div css={{ marginBottom: '16px', width: '100%' }}>
                        <IsPreOrderSwitcher fieldPrefix={fieldPrefix} onFilterChange={onFilterChange} />
                    </div>

                    {!isBrandPage && (
                        <BrandFiltersSection
                            currentFilterData={currentFilterData}
                            fieldPrefix={fieldPrefix}
                            expandedFilters={expandedFilters}
                            toggleFilter={toggleFilter}
                            onFilterChange={onFilterChange}
                        />
                    )}

                    {currentFilterData
                        .filter(filterField => !isBrandFilter(filterField))
                        .map(filterField => (
                            <MobileFilterItem
                                key={filterField.code}
                                filterField={filterField}
                                fieldPrefix={fieldPrefix}
                                expandedFilters={expandedFilters}
                                toggleFilter={toggleFilter}
                                onFilterChange={onFilterChange}
                            />
                        ))}
                </div>
            </div>
            <FiltersFooter
                isMobile
                fieldPrefix={fieldPrefix}
                currentTotalCount={currentTotalCount}
                currentRangeDefault={currentRangeDefault}
                setRememberedPrice={setRememberedPrice}
                onApplyClick={onApplyClick}
            />
        </div>
    );
};

export default MobileFiltersPopupContent;
